<template>
	<SportsLayout :context="miniContext">
		<template #backdrop>
			<Claim />
		</template>
		<ClientOnly>
			<div v-if="isDataLoading && !eventBucketsData" class="spinner">
				<Spinner color="light" name="crescent" />
			</div>
		</ClientOnly>

		<section v-if="eventBucketsData || !isDataLoading" class="all-sports">
			<CompetitionList :sportPageType="miniContext.pageType" />

			<article class="all-sports__sport_types">
				<Subheading>{{ $t('SPORTS_WEBAPP_SPORTS') }}</Subheading>

				<HiddenHorizontalScrollbar>
					<SportCard v-for="sport in sportOrder" :key="sport" :sport="sport" />
				</HiddenHorizontalScrollbar>
			</article>

			<AdUnit adPosition="sport_inlist_2" />

			<article class="all-sports__events">
				<Subheading>{{ $t('SPORTS_WEBAPP_LIVE_N_UPCOMING_EVENTS') }}</Subheading>

				<div v-for="({ sport, fullPath, events }, bucketIndex) in buckets" :key="sport" class="events-calendar">
					<SportHeader v-if="showEventHeader" :path="fullPath" :isIcon="true" :sportType="sport">
						<template #image>
							<img :alt="sport" :src="sportIconPath(sport)" />
						</template>

						<template #heading>{{ $t(`SPORTS_WEBAPP_SPORT_TYPE_${sport}`) }}</template>
					</SportHeader>

					<div class="events-calendar__events-container">
						<EventCard
							v-for="(event, eventIndex) in events"
							:key="event.node.id"
							:event="event.node"
							:bucketIndex="bucketIndex"
							:eventIndex="eventIndex"
							:context="getContext(event)"
						/>
					</div>
					<AdUnit v-if="bucketIndex === 0" adPosition="sport_inlist_3" />
				</div>
			</article>
			<AdUnit adPosition="sport_inlist_1" />
		</section>
	</SportsLayout>
</template>

<script setup lang="ts">
import AdUnit from '@/components/AdUnit.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import Claim from '@/features/sports/components/allsports/Claim.vue';
import CompetitionList from '@/features/sports/components/allsports/CompetitionList.vue';
import SportCard from '@/features/sports/components/allsports/SportCard.vue';
import SportHeader from '@/features/sports/components/ui/SportHeader.vue';
import SportsLayout from '@/features/sports/components/global/SportsLayout.vue';
import Subheading from '@/features/sports/components/ui/Subheading.vue';
import EventCard from '@/features/sports/components/global/EventCard.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { MultiStepSportEvent, Platform, SportType } from '@/@types/graphql-types';
import { SportPageType } from '@/features/sports/types/interfaces';
import { WebLocale } from '@/enums/web-locale';

import {
	GetAllSportsPageV2Query,
	GetAllSportsPageV2Document,
	GetAllSportsPageV2QueryVariables,
} from '@/features/sports/graphql/queries/GetAllSportsPageV2.query';

import { getOverviewUrlBySport } from '@/routing/sports';

import { isMultiStepSport, SportLocale } from '@/features/sports/types/enums';
import { eventsEdgeContext } from '@/features/sports/helpers/config';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { computed } from 'vue';

const sportIconPath = (sport: string) => `/${ASSETS_DIR}/img/sports/icons/sport/${sport.toLowerCase()}.png`;

const { webLocale, language, country } = storeToRefs(useLanguageStore());
const { todayDate } = usePersistedTodaysDate();

const queryVariables = computed<GetAllSportsPageV2QueryVariables>(() => {
	return {
		language: language.value,
		country: country.value,
		platform: Platform.Web,
		bucketSize: 9,
		first: 11,
		filter: {
			startAt: { from: todayDate.value.toISOString() },
			packages: [],
		},
		timezoneOffset: 0,
	};
});

const route = useRoute();
const isEnabled = computed(() => route.name === 'app.sports.subdomain.all');
const { result: eventBucketsData, loading: isDataLoading } = useQuery<
	GetAllSportsPageV2Query,
	GetAllSportsPageV2QueryVariables
>(GetAllSportsPageV2Document, queryVariables, {
	enabled: isEnabled,
	debounce: 100,
});

const miniContext = {
	sportType: 'GENERIC',
	pageType: SportPageType.AllSports,
} as const;

const buckets = computed(() => {
	const edges = eventBucketsData.value?.sportEventBucketsV2?.edges ?? [];

	if (edges == null || edges.length === 0) return [];

	let buckets = edges.map((bucket, i) => ({
		sport: edges[i].key.sport!.type,
		fullPath: getOverviewUrlBySport(edges[i].key.sport!.type, webLocale.value as SportLocale),
		events: (bucket.node?.edges ?? []).filter(edges => {
			if (edges == null) return false;
			return isMultiStepSport(edges.node.sport) ? (edges.node as MultiStepSportEvent).parent != null : true;
		}),
	}));

	if (country.value !== 'US') {
		buckets = buckets.filter(bucket => {
			return bucket.sport !== 'NASCAR';
		});
	}

	const cricketCountries = ['UK', 'GB', 'CA', 'US', 'QA', 'AE', 'KW', 'SA', 'BH', 'OM', 'JO', 'IQ', 'NL', 'IN'];
	if (!cricketCountries.includes(country.value)) {
		buckets = buckets.filter(bucket => {
			return bucket.sport !== 'CRICKET';
		});
	}

	buckets.sort((a, b) => sportOrder.value.indexOf(a.sport) - sportOrder.value.indexOf(b.sport));

	return buckets.slice(0, 6);
});

const sportOrder = computed(() => {
	switch (webLocale.value) {
		case WebLocale.us:
			return [
				SportType.AmericanFootball,
				SportType.Football,
				SportType.Tennis,
				SportType.Nascar,
				SportType.FormulaRacing,
				SportType.Baseball,
				SportType.Basketball,
				SportType.IceHockey,
				SportType.Rugby,
			];
		case WebLocale.ca:
			return [
				SportType.IceHockey,
				SportType.Basketball,
				SportType.Football,
				SportType.Tennis,
				SportType.FormulaRacing,
				SportType.AmericanFootball,
				SportType.Baseball,
				SportType.Cricket,
				SportType.Rugby,
			];
		case WebLocale.uk:
			return [
				SportType.Football,
				SportType.Tennis,
				SportType.Cricket,
				SportType.Rugby,
				SportType.FormulaRacing,
				SportType.Basketball,
				SportType.AmericanFootball,
				SportType.IceHockey,
				SportType.Baseball,
			];
		case WebLocale.in:
			return [
				SportType.Cricket,
				SportType.Football,
				SportType.Tennis,
				SportType.Basketball,
				SportType.FormulaRacing,
				SportType.Rugby,
				SportType.AmericanFootball,
				SportType.Baseball,
				SportType.IceHockey,
			];
		default:
			return [
				SportType.Football,
				SportType.Tennis,
				SportType.FormulaRacing,
				SportType.AmericanFootball,
				SportType.Baseball,
				SportType.Basketball,
				SportType.IceHockey,
				SportType.Rugby,
			];
	}
});

// hide sport event header as long as we only have 1 sport showing
const showEventHeader = computed(() => buckets.value.length > 1);

type SportEvent = NonNullable<
	NonNullable<GetAllSportsPageV2Query['sportEventBucketsV2']['edges']>[number]['node']['edges']
>[number];
function getContext(event: SportEvent) {
	if (isMultiStepSport(event.node.sport)) return {};

	return eventsEdgeContext(event.node.sport);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';

.spinner {
	width: 100%;
	height: 50vh;
	display: flex;
	align-items: center;
	justify-content: center;
	// to make it appear more in the viewport because the claim is taking up a lot of space
	transform: translateY(-50%);
}

.all-sports {
	> article {
		margin-bottom: 70px;

		@media (max-width: 550px) {
			margin-bottom: 45px;
		}

		> * + * {
			margin-top: 20px;

			@media (min-width: 550px) {
				margin-top: 32px;
			}
		}
	}

	&__events {
		.events-calendar {
			> * + * {
				margin-top: 20px;

				@media (min-width: 550px) {
					margin-top: 40px;
				}
			}

			&__events-container {
				@include layout-sport-columned;
			}
		}
	}
}
</style>
